import { type QueryObserverOptions, useQuery } from '@tanstack/react-query';

import FunctionKey from 'constants/functionKey';
import { useGetTokens } from 'libs/tokens';
import { useAccountAddress, useChainId } from 'libs/wallet';
import { generatePseudoRandomRefetchInterval } from 'utilities';
import type { Address } from 'viem';
import { type GetLiquidityHubInput, type GetLiquidityHubOutput, getLiquidityHub } from '..';
import { useGetCachedLiquidityHub } from '../useGetCachedLiquidityHub';

export type UseGetLiquidityHubInput = {
  vhTokenAddress: Address;
};

export type UseGetLiquidityHubQueryKey = [
  FunctionKey.GET_LIQUIDITY_HUB,
  Omit<GetLiquidityHubInput, 'tokens'>,
];

type Options = QueryObserverOptions<
  GetLiquidityHubOutput,
  Error,
  GetLiquidityHubOutput,
  GetLiquidityHubOutput,
  UseGetLiquidityHubQueryKey
>;

const refetchInterval = generatePseudoRandomRefetchInterval('fast');

export const useGetLiquidityHub = (input: UseGetLiquidityHubInput, options?: Partial<Options>) => {
  const { chainId } = useChainId();
  const { accountAddress } = useAccountAddress();
  const tokens = useGetTokens({ chainId });

  const queryInput: GetLiquidityHubInput = {
    chainId,
    accountAddress,
    tokens,
    vhTokenAddress: input.vhTokenAddress,
  };

  // Initialize proposal using cache if available
  const cachedLiquidityHub = useGetCachedLiquidityHub({
    chainId,
    accountAddress,
    vhTokenAddress: input.vhTokenAddress,
  });

  return useQuery({
    queryKey: [
      FunctionKey.GET_LIQUIDITY_HUB,
      { chainId, accountAddress, vhTokenAddress: input.vhTokenAddress },
    ],
    queryFn: () => getLiquidityHub(queryInput),
    refetchInterval,
    initialData: {
      liquidityHub: cachedLiquidityHub,
    },
    refetchOnMount: false,
    ...options,
    ...options,
  });
};
